import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { GridOutline, Options2Outline } from '@lyearn/icons';
import { Box, Checkbox, Stack, Typography } from '@lyearn/ui';
import RadioGroup from '@/components/RadioGroup';
import { RadioOption } from '@/components/RadioGroup/types';
import { PropertiesToDuplicateEnum } from '@/types/schema';

import {
  getListPropertiesToDuplicateOptions,
  getTaskPropertiesToDuplicateOptions,
  ListPropertiesToDuplicateEnum,
} from '../../utils';
import { ListDuplicationFormPropertiesType } from '../Editor/config';

interface PropertiesToDuplicateSelectorProps {
  value: ListDuplicationFormPropertiesType;
  onChange: (value: ListDuplicationFormPropertiesType) => void;
}

enum RadioEnum {
  EVERYTHING = 'everything',
  CUSTOM = 'custom',
}

function PropertiesToDuplicateSelector({ value, onChange }: PropertiesToDuplicateSelectorProps) {
  const { t } = useTranslation();
  const [radioSelection, setRadioSelection] = useState<RadioEnum>(RadioEnum.EVERYTHING);

  const radioOptions = useMemo(
    () => [
      {
        id: RadioEnum.EVERYTHING,
        label: t('Task:DuplicationProperties.Everything'),
        Icon: GridOutline,
      },
      {
        id: RadioEnum.CUSTOM,
        label: t('Task:DuplicationProperties.Custom'),
        Icon: Options2Outline,
      },
    ],
    [t],
  );

  const taskOptions = useMemo(() => getTaskPropertiesToDuplicateOptions(t), [t]);
  const projectOptions = useMemo(() => getListPropertiesToDuplicateOptions(t), [t]);

  const handleRadioChange = useCallback(
    (_: string, changedOption: RadioOption<RadioEnum>) => {
      if (changedOption.id === RadioEnum.EVERYTHING) {
        onChange({
          listPropertiesToDuplicate: projectOptions.map((option) => option.value),
          actionItemPropertiesToDuplicate: taskOptions.map((option) => option.value),
        });
      }
      setRadioSelection(changedOption.id);
    },
    [projectOptions, taskOptions, onChange],
  );

  const toggleTaskProperty = useCallback(
    (property: PropertiesToDuplicateEnum) => {
      const properties = value.actionItemPropertiesToDuplicate;
      onChange({
        ...value,
        actionItemPropertiesToDuplicate: properties.includes(property)
          ? properties.filter((p) => p !== property)
          : [...properties, property],
      });
    },
    [value, onChange],
  );

  const toggleListProperty = useCallback(
    (property: ListPropertiesToDuplicateEnum) => {
      const properties = value.listPropertiesToDuplicate;
      onChange({
        ...value,
        listPropertiesToDuplicate: properties.includes(property)
          ? properties.filter((p) => p !== property)
          : [...properties, property],
      });
    },
    [value, onChange],
  );

  const handleSelectAllClick = useCallback(() => {
    const noValuesSelected =
      value.listPropertiesToDuplicate.length === 0 &&
      value.actionItemPropertiesToDuplicate.length === 0;
    if (noValuesSelected) {
      onChange({
        listPropertiesToDuplicate: projectOptions.map((option) => option.value),
        actionItemPropertiesToDuplicate: taskOptions.map((option) => option.value),
      });
    } else {
      onChange({
        listPropertiesToDuplicate: [],
        actionItemPropertiesToDuplicate: [],
      });
    }
  }, [value, projectOptions, taskOptions, onChange]);

  const selectedPropertiesCount =
    value.listPropertiesToDuplicate.length + value.actionItemPropertiesToDuplicate.length;

  return (
    <Stack className="w-full" direction="col" gapY={32}>
      <RadioGroup<RadioEnum>
        className="w-full grid grid-cols-2"
        direction="row"
        gap={12}
        options={radioOptions}
        selectedId={radioSelection}
        type="box"
        onChange={handleRadioChange}
      />

      {radioSelection === RadioEnum.EVERYTHING ? (
        <Typography className="ms-8" variant="body-short-01">
          {t('Task:DuplicateList.DuplicateEverythingDescription')}
        </Typography>
      ) : (
        <Stack className="w-full" direction="col" gapY={24}>
          <Typography className="ms-8" variant="body-short-01">
            {t('Task:DuplicationProperties.CustomDescription')}
          </Typography>

          <Stack className="w-full border border-border rounded-8" direction="col">
            <Stack
              alignItems="center"
              className="w-full py-12 ps-18 pe-8 border-b border-border cursor-pointer"
              direction="row"
              justifyContent="between"
              role="button"
              onClick={handleSelectAllClick}>
              <Typography variant="label-02">{t('Common:Filter.SelectAll')}</Typography>
              <Checkbox
                checked={taskOptions.length + projectOptions.length === selectedPropertiesCount}
                indeterminate={
                  selectedPropertiesCount > 0 &&
                  selectedPropertiesCount < taskOptions.length + projectOptions.length
                }
              />
            </Stack>

            <Stack className="w-full p-16" direction="col" gapY={24}>
              <Stack className="w-full" direction="col" gapY={16}>
                <Typography className="uppercase" color="text-text-placeholder" variant="label-03">
                  {t('Task:DuplicationProperties.TaskProperties')}
                </Typography>
                <Box className="w-full grid grid-cols-2 gap-y-12">
                  {taskOptions.map((option) => (
                    <Stack
                      key={option.value}
                      alignItems="center"
                      className="cursor-pointer"
                      direction="row"
                      gapX={8}
                      justifyContent="start"
                      role="button"
                      onClick={() => toggleTaskProperty(option.value)}>
                      <Checkbox
                        key={option.value}
                        checked={value.actionItemPropertiesToDuplicate.includes(option.value)}
                      />
                      <Typography variant="body-short-01">{option.label}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Stack>

            <Stack className="w-full p-16" direction="col" gapY={24}>
              <Stack className="w-full" direction="col" gapY={16}>
                <Typography className="uppercase" color="text-text-placeholder" variant="label-03">
                  {t('Task:DuplicationProperties.ListProperties')}
                </Typography>
                <Box className="w-full grid grid-cols-2 gap-y-12">
                  {projectOptions.map((option) => (
                    <Stack
                      key={option.value}
                      alignItems="center"
                      className="cursor-pointer"
                      direction="row"
                      gapX={8}
                      justifyContent="start"
                      role="button"
                      onClick={() => toggleListProperty(option.value)}>
                      <Checkbox
                        key={option.value}
                        checked={value.listPropertiesToDuplicate.includes(option.value)}
                      />
                      <Typography variant="body-short-01">{option.label}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default PropertiesToDuplicateSelector;
